import { Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import React from 'react';

import { useBridgeSubmission } from 'src/components/forms/hooks/useBridgeSubmission';
import { useVisitReason } from 'src/components/forms/hooks/useVisitReason';
import FieldGroup from 'src/components/general/fieldGroup';
import { BridgeSubmissionReason } from 'src/dropInClinic/bridgeSubmissionReason/BridgeSubmissionReason';
import { VisitReason } from 'src/dropInClinic/visitReason/VisitReason';
import { AttendeeList } from 'src/events/components/AttendeeList';
import { EventLocationVerification } from 'src/events/components/EventLocationVerification';
import { PatientAssessments } from 'src/events/components/PatientAssessments';
import { PharmacyChangeRequest } from 'src/events/components/PharamacyChangeRequest';
import { PrimaryConcern } from 'src/events/components/PrimaryConcern';
import { useStyles as usePreVisitDisplayStyles } from 'src/events/styles/preVisitDisplay.styles';
import type { Patient } from 'src/shared/stores/resource';
import { EventInstance } from 'src/stores/models/event';

export const ViewEventDetails: React.FC<{
  event: EventInstance;
}> = ({ event: { id, subType, title, attendees, signedAt, patientAttendee } }) => {
  const visitReason = useVisitReason(id);
  const bridgeSubmissionReason = useBridgeSubmission(id);
  const preVisitDisplayStyles = usePreVisitDisplayStyles();
  const styles = useStyles();

  const providers = attendees.filter(attendee => attendee.__typename === 'Provider');
  const patient =
    patientAttendee ??
    attendees.find((attendee): attendee is Patient => attendee.__typename === 'Patient');

  // A visit is complete once the event signedAt value is populated
  const isVisitComplete = !!signedAt;

  return (
    <>
      {title && (
        <FieldGroup label="Title">
          <Typography style={{ wordBreak: 'break-word' }} variant="body2">
            {title}
          </Typography>
        </FieldGroup>
      )}

      {!!providers.length && <AttendeeList attendees={providers} />}

      <EventLocationVerification eventId={id} />

      <PrimaryConcern patient={patient} subType={subType} />

      {visitReason && (
        <div className={preVisitDisplayStyles.sectionContainer}>
          <div className={preVisitDisplayStyles.sectionLabel}>Visit Reason</div>
          <div className={styles.listContainer}>
            <VisitReason visitReasonDetails={visitReason.details} />
          </div>
        </div>
      )}
      {bridgeSubmissionReason && (
        <div className={preVisitDisplayStyles.sectionContainer}>
          <div className={preVisitDisplayStyles.sectionLabel}>Bridge Submission Reason</div>
          <div className={styles.listContainer}>
            <BridgeSubmissionReason bridgeSubmissionDetails={bridgeSubmissionReason} />
          </div>
        </div>
      )}

      <PatientAssessments patientId={patient?.id} isVisitComplete={isVisitComplete} />

      <PharmacyChangeRequest visitReasonDetails={visitReason?.details} />
    </>
  );
};

const useStyles = makeStyles({
  listContainer: {
    '& ul': {
      marginTop: 5,
      fontSize: '0.875rem',
    },
    '& span': {
      fontWeight: 'bold',
    },
  },
});
